import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllSubscriptions, getAddOns } from "../../services/subscriptionsApi";
import { initiatePayment } from "../../services/paymentsApi";
import { useAuth } from "../../context/AuthContext";
import { useSubscription } from "../../context/SubscriptionContext";
import { yearlyTermActive } from "../../utils/yearlyTerm";
import styles from "./UpsellModal.module.css";

/**
 * Shown when a tenant hits a plan/quota limit. Instead of a dead-end error it presents the packs
 * the user can buy and starts the payment flow inline. Only ADMINs can purchase (the payment
 * endpoints require CAN_EDIT_SUBSCRIPTION); everyone else gets a "contact your admin" message.
 *
 * Add-on packs are offered above the plans: topping up the one quota you ran out of is almost
 * always cheaper and less disruptive than moving the whole tenant onto a bigger plan.
 *
 * @param limitCode  optional LIMIT code (e.g. "PDF_PAGE_LIMIT") of the quota that was hit. When
 *                   given, only add-ons that top that quota up are shown, so hitting the student
 *                   cap doesn't advertise PDF pages. Callers that don't know it show every add-on.
 */
const UpsellModal = ({ isOpen, message, limitCode, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const [plans, setPlans] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buyingCode, setBuyingCode] = useState(null);

  // OWNER manages the tenant's billing too (the SPA treats OWNER as ADMIN, and the backend grants it
  // the subscription privileges), so both can purchase. Others get the "contact your admin" message.
  const canPurchase = !!user?.roles?.some((r) => r === "ADMIN" || r === "OWNER");

  useEffect(() => {
    if (!isOpen || !canPurchase) return;
    let active = true;
    setLoading(true);
    // Both are optional: a tenant with no subscription row gets an error from /add-ons, and that
    // must not stop the plans from rendering — so each side falls back to an empty list on its own.
    Promise.all([
      getAllSubscriptions().catch(() => []),
      getAddOns().catch(() => []),
    ])
      .then(([planData, addOnData]) => {
        if (!active) return;
        const list = (d) => (Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : []);
        setPlans(list(planData));
        setAddOns(list(addOnData));
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [isOpen, canPurchase]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // While a yearly plan has prepaid months left, the backend rejects every other subscription
  // plan — the only yearly purchase it accepts is extending the CURRENT one. This modal is the
  // "limit reached" impulse surface: never show a card whose Buy would 400.
  const yearlyActive = yearlyTermActive(subscription);
  const yearly = useMemo(() => {
    const list = plans.filter((p) => p.billingType === "YEARLY");
    return yearlyActive ? list.filter((p) => p.code === subscription.code) : list;
  }, [plans, yearlyActive, subscription]);
  const monthly = useMemo(
    () => (yearlyActive ? [] : plans.filter((p) => p.billingType === "MONTHLY")),
    [plans, yearlyActive]
  );
  const oneTime = useMemo(() => plans.filter((p) => p.billingType === "ONE_TIME"), [plans]);

  // Narrow to the quota the user actually ran out of, when the caller knew which one that was.
  const relevantAddOns = useMemo(() => {
    if (!limitCode) return addOns;
    return addOns.filter((a) => (a.items || []).some((i) => i.limitCode === limitCode));
  }, [addOns, limitCode]);

  if (!isOpen) return null;

  const startPayment = async (code, params) => {
    if (buyingCode) return;
    try {
      setBuyingCode(code);
      const returnPath = window.location.pathname + window.location.search;
      const res = await initiatePayment({ ...params, returnPath });
      sessionStorage.setItem("gredus.kapitalPaymentTxId", String(res.paymentTransactionId));
      sessionStorage.setItem("gredus.kapitalReturnPath", returnPath);
      window.location.href = res.redirectUrl;
    } catch (e) {
      setBuyingCode(null);
      toast.error(e?.response?.data?.message || t("pages.upsell.buyError"));
    }
  };

  const handleBuy = (plan) =>
    startPayment(plan.code, {
      purpose: plan.billingType === "ONE_TIME" ? "ONE_TIME_PACKAGE" : "RENEWAL",
      planCode: plan.code,
    });

  // Add-ons resolve server-side against the tenant's CURRENT plan, so only the code travels.
  const handleBuyAddOn = (addOn) =>
    startPayment(addOn.code, { purpose: "ADDON_PURCHASE", addOnCode: addOn.code });

  const goToPlans = () => {
    onClose?.();
    navigate("/settings/subscription");
  };

  const renderCard = (plan, oneTimePack) => (
    <div key={plan.id} className={styles.card}>
      <div className={styles.cardName}>{plan.name}</div>
      <div className={styles.cardPrice}>
        <span className={styles.priceValue}>{plan.price}</span>
        <span className={styles.priceUnit}>
          {t("pages.upsell.currency")}{" "}
          {plan.billingType === "YEARLY"
            ? t("pages.upsell.perYear", { defaultValue: "/il" })
            : oneTimePack
              ? t("pages.upsell.oneTimeLabel")
              : t("pages.upsell.perMonth")}
        </span>
      </div>
      {Array.isArray(plan.limits) && plan.limits.length > 0 && (
        <ul className={styles.limits}>
          {plan.limits.slice(0, 4).map((l, i) => (
            <li key={i}>
              {/* Video quota is stored in SECONDS (V23); show minutes. */}
              {l.limitName}: <strong>{l.limitValue === -1 ? "∞"
                : l.limitCode === "VIDEO_MINUTES_LIMIT" ? Math.round(l.limitValue / 60)
                : l.limitValue}</strong>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className={styles.buyBtn}
        onClick={() => handleBuy(plan)}
        disabled={!!buyingCode}
      >
        {buyingCode === plan.code ? t("pages.upsell.redirecting") : t("pages.upsell.buy")}
      </button>
    </div>
  );

  const renderAddOnCard = (addOn) => (
    <div key={addOn.code} className={`${styles.card} ${styles.addOnCard}`}>
      <div className={styles.cardName}>{addOn.name}</div>
      <div className={styles.cardPrice}>
        <span className={styles.priceValue}>{addOn.price}</span>
        <span className={styles.priceUnit}>
          {t("pages.upsell.currency")} {t("pages.upsell.oneTimeLabel")}
        </span>
      </div>
      {Array.isArray(addOn.items) && addOn.items.length > 0 && (
        <ul className={styles.limits}>
          {addOn.items.map((item, i) => (
            <li key={i}>
              {item.name}: <strong>+{item.value}</strong>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className={styles.buyBtn}
        onClick={() => handleBuyAddOn(addOn)}
        disabled={!!buyingCode}
      >
        {buyingCode === addOn.code ? t("pages.upsell.redirecting") : t("pages.upsell.buy")}
      </button>
    </div>
  );

  return (
    <div
      className={styles.overlay}
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className={styles.modal} role="dialog" aria-modal="true">
        <button type="button" className={styles.close} onClick={onClose} aria-label={t("common.close", { defaultValue: "×" })}>
          ×
        </button>

        <div className={styles.header}>
          <span className={styles.icon} aria-hidden>🚀</span>
          <h2 className={styles.title}>{t("pages.upsell.title")}</h2>
          <p className={styles.subtitle}>{t("pages.upsell.subtitle")}</p>
          {message && <div className={styles.limitMsg}>{message}</div>}
        </div>

        <div className={styles.body}>
          {!canPurchase ? (
            <div className={styles.adminOnly}>{t("pages.upsell.adminOnly")}</div>
          ) : loading ? (
            <div className={styles.loading}>{t("pages.upsell.loadingPlans")}</div>
          ) : yearly.length === 0 && monthly.length === 0 && oneTime.length === 0 && relevantAddOns.length === 0 ? (
            <div className={styles.loading}>{t("pages.upsell.empty")}</div>
          ) : (
            <>
              {/* First, because topping up the exhausted quota is the cheapest way out. */}
              {relevantAddOns.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>{t("pages.upsell.addOnPacks")}</h3>
                  <div className={styles.grid}>{relevantAddOns.map(renderAddOnCard)}</div>
                </section>
              )}
              {yearly.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    {t("pages.upsell.yearlyPlans", { defaultValue: "İllik abunəlik" })}{" "}
                    <span
                      style={{
                        marginLeft: 4, padding: "2px 8px", borderRadius: 5,
                        background: "#2563eb", color: "#fff", fontSize: 10.5, fontWeight: 700,
                        verticalAlign: "middle",
                      }}
                    >
                      {t("pages.upsell.recommended", { defaultValue: "Tövsiyə olunur" })}
                    </span>
                  </h3>
                  <div className={styles.grid}>{yearly.map((p) => renderCard(p, false))}</div>
                </section>
              )}
              {monthly.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>{t("pages.upsell.monthlyPlans")}</h3>
                  <div className={styles.grid}>{monthly.map((p) => renderCard(p, false))}</div>
                </section>
              )}
              {oneTime.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>{t("pages.upsell.oneTimePackages")}</h3>
                  <div className={styles.grid}>{oneTime.map((p) => renderCard(p, true))}</div>
                </section>
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          {canPurchase && (
            <button type="button" className={styles.linkBtn} onClick={goToPlans}>
              {t("pages.upsell.seeAllPlans")}
            </button>
          )}
          <button type="button" className={styles.laterBtn} onClick={onClose}>
            {t("pages.upsell.later")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpsellModal;
